import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Alert,
    AlertIcon,
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Input,
    Spinner,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import AsyncSelect from "react-select/async";
import { fetchFacilitiesBySearch, fetchMonthlyPickupReport } from "../../api";
import AccessDenied from "../../components/AccessDenied";
import TitleHeader from "../../components/TitleHeader";
import { hasPermission } from "../../Utils";

import { Parser } from "@json2csv/plainjs";
import { FaDownload } from "react-icons/fa";


const groupByMonth = (data) => {
    const grouped = {};
    data.forEach((row) => {
        const [m, , y] = row.date.split("/");
        const key = `${y}-${m}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(row);
    });
    return grouped;
};

const formatMonthTitle = (key) => {
    const [y, m] = key.split("-");
    return new Date(`${y}-${m}-01`).toLocaleString("en-US", {
        month: "long",
        year: "numeric",
    });
};

const MonthlyPickupsAccordionReport = () => {
    const [searchParams, setSearchParams] = useState({
        facilityId: "",
        startDate: "",
        endDate: "",
    });

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState(null);

    const onInputChange = (e) => {
        const { name, value } = e.target;
        setSearchParams((prev) => ({ ...prev, [name]: value }));
    };

    const clearSearch = () => {
        setSearchParams({ facilityId: "", startDate: "", endDate: "" });
        setIsSearching(false);
        setData([]);
        setError(null);
    };

    const handleSearch = async () => {
        setIsSearching(true);
        setLoading(true);
        setError(null);

        try {
            const result = await fetchMonthlyPickupReport(searchParams);
            if (result.success) setData(result.data || []);
            else setError(result.message || "No data");
        } catch {
            setError("Failed to load report");
        }

        setLoading(false);
    };

    const monthlyData = useMemo(() => groupByMonth(data), [data]);

    if (!hasPermission("viewReports")) {
        return <AccessDenied msg="You don't have permission to access reports page" />;
    }

    const handleExportCSV = () => {
        if (!data.length) return;

        // ✅ Group by month (same as accordion)
        const grouped = {};
        data.forEach(row => {
            const [m, , y] = row.date.split("/");
            const key = `${y}-${m}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(row);
        });

        // ✅ Sort months
        const sortedMonths = Object.keys(grouped).sort(
            (a, b) => new Date(a) - new Date(b)
        );

        const flatData = [];

        sortedMonths.forEach(monthKey => {
            const [y, m] = monthKey.split("-");
            const monthName = new Date(`${y}-${m}-01`).toLocaleString("en-US", {
                month: "long",
                year: "numeric",
            });

            // ✅ Month title row
            flatData.push({
                Date: monthName,
                "Pickup Time": "",
                "Samples Count": "",
            });

            // ✅ Sort days inside month
            grouped[monthKey]
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .forEach(row => {
                    flatData.push({
                        Date: row.date,
                        "Pickup Time": row.pickupTime
                            ? new Date(row.pickupTime).toLocaleTimeString()
                            : "",
                        "Samples Count": row.totalSamples,
                    });
                });

            // ✅ Empty row after each month
            flatData.push({
                Date: "",
                "Pickup Time": "",
                "Samples Count": "",
            });
        });

        const parser = new Parser();
        const csv = parser.parse(flatData);

        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `pickup_report_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
    };



    return (
        <Box maxW="full" mx="auto" p="8">
            <TitleHeader title="Monthly Pickups Report" />

            <Heading size="md" mt={10}>Run your Report</Heading>

            {/* ✅ Filters */}
            <Box mb={4} mt={5} p={4} bg="gray.50" borderRadius="md" boxShadow="sm">
                <HStack spacing={4}>
                    <FormControl>
                        <FormLabel>Facility</FormLabel>
                        <AsyncSelect
                            cacheOptions
                            loadOptions={fetchFacilitiesBySearch}
                            defaultOptions
                            onChange={(option) =>
                                setSearchParams((prev) => ({
                                    ...prev,
                                    facilityId: option ? option.value : "",
                                }))
                            }
                            placeholder="Search and select a facility"
                            isClearable
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Start Date (MM/DD/YYYY)</FormLabel>
                        <Input
                            name="startDate"
                            placeholder="MM/DD/YYYY"
                            value={searchParams.startDate}
                            onChange={onInputChange}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>End Date (MM/DD/YYYY)</FormLabel>
                        <Input
                            name="endDate"
                            placeholder="MM/DD/YYYY"
                            value={searchParams.endDate}
                            onChange={onInputChange}
                        />
                    </FormControl>

                    <Button mt={7} colorScheme="blue" onClick={handleSearch}>
                        <FaSearch />
                    </Button>

                    {isSearching && (
                        <Button mt={7} colorScheme="red" onClick={clearSearch}>
                            <FaTimes />
                        </Button>
                    )}
                </HStack>
            </Box>

            {/* ✅ Results */}
            {isSearching && (
                <Card mt={6}>
                    <CardHeader>
                        <Heading size="md">Monthly Breakdown</Heading>
                    </CardHeader>
                    <CardBody>
                        {loading ? (
                            <Spinner size="lg" />
                        ) : error ? (
                            <Alert status="error">
                                <AlertIcon />
                                {error}
                            </Alert>
                        ) : (
                            <><Button
                                size="sm"
                                colorScheme="green"
                                leftIcon={<FaDownload />}
                                onClick={handleExportCSV}
                            >
                                Export CSV
                            </Button>

                                <Accordion allowMultiple>
                                    {Object.keys(monthlyData)
                                        .sort((a, b) => new Date(a) - new Date(b))
                                        .map((monthKey) => (
                                            <AccordionItem key={monthKey}>
                                                <AccordionButton>
                                                    <Box flex="1" textAlign="left" fontWeight="bold">
                                                        {formatMonthTitle(monthKey)}
                                                    </Box>
                                                    <AccordionIcon />
                                                </AccordionButton>

                                                <AccordionPanel pb={4}>
                                                    <Table size="sm">
                                                        <Thead>
                                                            <Tr>
                                                                <Th>Date</Th>
                                                                <Th isNumeric>Samples Received</Th>
                                                                <Th>Pickup</Th>
                                                            </Tr>
                                                        </Thead>
                                                        <Tbody>
                                                            {monthlyData[monthKey]
                                                                .sort((a, b) => new Date(a.date) - new Date(b.date))
                                                                .map((row, i) => (
                                                                    <Tr key={i}>
                                                                        <Td>{row.date}</Td>
                                                                        <Td isNumeric>{row.totalSamples}</Td>
                                                                        <Td>{row.pickupTime ? new Date(row.pickupTime).toLocaleTimeString() : "—"}</Td>
                                                                    </Tr>
                                                                ))}
                                                        </Tbody>
                                                    </Table>
                                                </AccordionPanel>
                                            </AccordionItem>
                                        ))}
                                </Accordion>
                            </>
                        )}
                    </CardBody>
                </Card>
            )}
        </Box>
    );
};

export default MonthlyPickupsAccordionReport;
